import type {
  SystemEvidenceRecord,
  SystemScenarioDefinition,
  SystemScenarioHandler,
  SystemScenarioHandlerOutput,
} from '@/governor/system-workspaces/SystemWorkspaceContracts';

import {
  hasSystemScenarioRegistration,
  registerSystemScenario,
} from '@/governor/system-workspaces/SystemScenarioRegistry';

import {
  TREE_RUNTIME_SCENARIOS,
  type TreeRuntimeScenarioId,
} from './TreeRuntimeSimulator';

import {
  runTreeCorrectnessFixture,
} from './TreeCorrectnessFixtureEngine';

export const TREE_SIMULATOR_SYSTEM_ID =
  'tree';

export const TREE_CORRECTNESS_SCENARIO_IDS =
  TREE_RUNTIME_SCENARIOS.map(
    scenario => scenario.id,
  ) as TreeRuntimeScenarioId[];

function scenarioName(
  scenarioId: TreeRuntimeScenarioId,
): string {
  return scenarioId
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter =>
      letter.toUpperCase(),
    );
}

export const treeCorrectnessScenarios:
  SystemScenarioDefinition[] =
  TREE_RUNTIME_SCENARIOS.map(scenario => ({
    id: scenario.id,
    systemId: TREE_SIMULATOR_SYSTEM_ID,
    name: scenarioName(scenario.id),
    description: scenario.purpose,
    mode: 'SIMULATED',
    destructive: false,
    productionWrite: false,
    inputs: {
      deterministic: true,
      fixtureEngine:
        'TreeCorrectnessFixtureEngine',
    },
    expectations:
      scenario.expectedEvidence.map(
        evidenceId => ({
          id: evidenceId,
          description:
            `Fixture must produce ${evidenceId}.`,
          required: true,
        }),
      ),
  }));

function createEvidence(
  scenarioId: TreeRuntimeScenarioId,
  runId: string,
  observedAt: string,
  summary: string,
  passed: boolean,
  actual: Record<string, unknown>,
): SystemEvidenceRecord {
  return {
    id: `${scenarioId}-fixture-evidence`,
    systemId: TREE_SIMULATOR_SYSTEM_ID,
    mode: 'SIMULATED',
    category: 'simulation',
    source:
      'TreeCorrectnessFixtureEngine',
    summary,
    observedAt,
    critical: true,
    passed,
    correlationId: runId,
    payload: {
      scenarioId,
      deterministic: true,
      isolated: true,
      productionWrite: false,
      actual,
    },
  };
}

function createHandler(
  scenarioId: TreeRuntimeScenarioId,
): SystemScenarioHandler {
  return {
    scenarioId,
    systemId: TREE_SIMULATOR_SYSTEM_ID,

    execute: async ({
      runId,
      now,
      signal,
    }): Promise<SystemScenarioHandlerOutput> => {
      if (signal.aborted) {
        throw new Error(
          'Tree simulation was aborted before execution.',
        );
      }

      const result =
        runTreeCorrectnessFixture(
          scenarioId,
        );

      return {
        passed: result.passed,
        actual: {
          ...result.actual,
          deterministic: true,
          isolated: true,
          productionWrite: false,
        },
        evidence: [
          createEvidence(
            scenarioId,
            runId,
            now(),
            result.summary,
            result.passed,
            result.actual,
          ),
        ],
        productionMutationOccurred: false,
      };
    },

    cleanup: async () => {
      return;
    },
  };
}

export function registerTreeCorrectnessScenarioPackage():
  void {
  for (
    const scenario of
    treeCorrectnessScenarios
  ) {
    const scenarioId =
      scenario.id as TreeRuntimeScenarioId;

    if (
      hasSystemScenarioRegistration(
        scenario.systemId,
        scenario.id,
      )
    ) {
      continue;
    }

    registerSystemScenario(
      scenario,
      createHandler(scenarioId),
    );
  }
}
